/**
 * Опрос сервера, который не топит сам себя.
 *
 * Так это выглядело до появления файла: колокольчик дёргал три ручки каждые
 * пять секунд, поздравлялка — ещё одну каждые семь, и ни один из них не ждал
 * предыдущего ответа. Замер в браузере: за первые двадцать секунд загрузки
 * терминала уходило шестьдесят запросов к /api, и в этой очереди запрос
 * истории спреда для графика ждал 15.8 секунды — человек всё это время видел
 * надпись «Загружаем историю спреда…» и считал график сломанным.
 *
 * Бэкенд однопоточный: очередь не ускоряет ответы, она их растягивает.
 * Поэтому здесь три правила:
 *   1. пока предыдущий запрос в полёте, новый не отправляем;
 *   2. на скрытой вкладке не опрашиваем вовсе — данные всё равно никто не
 *      видит, а очередь общая с той вкладкой, где смотрят;
 *   3. вернувшись на вкладку, обновляемся сразу, а не ждём следующего тика.
 *
 * И четвёртое, без которого первое опасно: запрос, повисший без ответа и без
 * ошибки (смена сети, обрыв Wi-Fi, полуоткрытое соединение — таймаута у
 * запросов нет), не должен останавливать опрос навсегда. Таблица сканера при
 * этом застывала молча, вместе со звуком о новых связках. Запрос, который
 * летит дольше `stallMs`, считаем потерянным и шлём следующий; его поздний
 * ответ на новый запрос уже не влияет.
 */
import { useEffect, useRef } from 'react';

/** Сколько ждём ответ, прежде чем считать запрос потерянным: не меньше 30 с
 *  (тяжёлые ручки под нагрузкой отвечали по 15 с) и не меньше трёх шагов. */
export function stallAfterMs(intervalMs: number): number {
  return Math.max(30_000, intervalMs * 3);
}

/**
 * Пропуск «один запрос за раз» с отпусканием по времени. `enter()` — номер
 * запроса либо 0, если прошлый ещё летит и не просрочен; `leave(n)` снимает
 * занятость, только если `n` — последний выданный номер.
 */
export function flightGate(stallMs: number, now: () => number = Date.now) {
  let seq = 0;
  let busy = 0;        // номер запроса в полёте, 0 — свободно
  let since = 0;
  return {
    enter(): number {
      if (busy && now() - since < stallMs) return 0;
      busy = ++seq;
      since = now();
      return busy;
    },
    leave(n: number): void {
      if (busy === n) busy = 0;
    },
  };
}

interface Options {
  /** Шаг опроса в миллисекундах. */
  intervalMs: number;
  /** Задержка перед первым запросом — дать подняться авторизации и сокету. */
  initialDelayMs?: number;
  /** Опрашивать ли на скрытой вкладке (по умолчанию нет). */
  whenHidden?: boolean;
  /** Через сколько повисший запрос перестаёт держать опрос (см. stallAfterMs). */
  stallMs?: number;
}

export function usePolling(fn: () => Promise<void>, deps: unknown[], opts: Options) {
  const { intervalMs, initialDelayMs = 0, whenHidden = false } = opts;
  const stallMs = opts.stallMs ?? stallAfterMs(intervalMs);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let alive = true;
    const gate = flightGate(stallMs);
    let timer: number | undefined;

    const run = async () => {
      if (!alive) return;
      if (!whenHidden && document.visibilityState === 'hidden') return;
      const n = gate.enter();
      if (!n) return;
      try {
        await fnRef.current();
      } finally {
        gate.leave(n);
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') void run();
    };

    const start = window.setTimeout(() => {
      void run();
      timer = window.setInterval(run, intervalMs);
    }, initialDelayMs);

    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      window.clearTimeout(start);
      if (timer) window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, initialDelayMs, whenHidden, stallMs, ...deps]);
}

/**
 * Тот же опрос для эффекта, в котором кроме запроса живёт ещё что-то
 * (подписка на сокет): первый запрос сразу, дальше раз в `intervalMs`, без
 * наложения, без опроса на скрытой вкладке и с обновлением при возврате на
 * неё. Возвращает функцию остановки — её зовёт очистка эффекта.
 */
export function startPolling(
  fn: () => Promise<void>,
  intervalMs: number,
  opts: {
    /** Опрашивать ли на скрытой вкладке: спрашивается на каждом тике (сканер
     *  со звуком или системными уведомлениями обязан работать и в фоне). */
    whenHidden?: () => boolean;
    /** Первый запрос уже сделан вызывающим — начать со следующего шага. */
    skipFirst?: boolean;
    /** Через сколько повисший запрос перестаёт держать опрос (см. stallAfterMs). */
    stallMs?: number;
  } = {},
): () => void {
  let alive = true;
  const gate = flightGate(opts.stallMs ?? stallAfterMs(intervalMs));
  const run = async () => {
    if (!alive) return;
    if (document.visibilityState === 'hidden' && !opts.whenHidden?.()) return;
    const n = gate.enter();
    if (!n) return;
    try {
      await fn();
    } catch { /* опрос переживает разовую ошибку */ } finally {
      gate.leave(n);
    }
  };
  const onVisible = () => {
    if (document.visibilityState === 'visible') void run();
  };
  if (!opts.skipFirst) void run();
  const timer = window.setInterval(run, intervalMs);
  document.addEventListener('visibilitychange', onVisible);
  return () => {
    alive = false;
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
  };
}

/** Обёртка для голого setInterval: на скрытой вкладке шаг пропускается.
 *  Для мест, где переводить на usePolling — менять устройство эффекта. */
export const whenVisible = (fn: () => unknown) => () => {
  if (document.visibilityState !== 'hidden') void fn();
};
